/* eslint import/no-webpack-loader-syntax: off */
import {useEffect, useMemo, useRef, useState} from 'react'
import {bbox as turfBbox} from '@turf/turf'
import mapboxgl from '!mapbox-gl'
import {useTranslation} from 'react-i18next'

import {getCopilotAmbientProductivity} from './copilotModel'

const EMPTY_FEATURE_COLLECTION = {type: 'FeatureCollection', features: []}
const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || ''

const AMBIENT_COLORS = {
  MAP: '#16784a',
  AP: '#4ea95f',
  PPA: '#8bc76b',
  PP: '#e6c84f',
  PPB: '#eda04c',
  BP: '#dc694d',
  MBP: '#b93852',
}

const MAP_LAYER_DEFINITIONS = [
  {
    id: 'historicalAmbient',
    translationKey: 'historical_ambient',
    color: '#7c3aed',
    type: 'outline',
  },
  {
    id: 'soilMap',
    translationKey: 'soil_map',
    color: '#111827',
    type: 'outline',
  },
  {
    id: 'terrain',
    translationKey: 'terrain',
    colors: ['#40205f', '#a66f78', '#f0d77a'],
    property: 'meanElevationM',
    unit: 'm',
    type: 'metric',
  },
  {
    id: 'ndvi',
    translationKey: 'ndvi',
    colors: ['#d73027', '#fee08b', '#1a9850'],
    property: 'ndviValue',
    type: 'metric',
  },
  {
    id: 'evi',
    translationKey: 'evi',
    colors: ['#f08a24', '#f4e7a1', '#0891b2'],
    property: 'eviValue',
    type: 'metric',
  },
  {
    id: 'yield',
    translationKey: 'yield',
    colors: ['#c62828', '#f4cf65', '#159447'],
    property: 'economicYieldTonsHa',
    unit: 't/ha',
    type: 'metric',
  },
  {
    id: 'grossMargin',
    translationKey: 'gross_margin',
    colors: ['#b91c1c', '#f59e0b', '#0f766e'],
    property: 'grossMarginUsdHa',
    unit: 'USD/ha',
    type: 'metric',
  },
]

const RENDER_LAYER_IDS = {
  historicalAmbient: [
    'copilot-map-ambient-hit',
    'copilot-map-ambient-line',
    'copilot-map-selected-ambient-fill',
  ],
  soilMap: ['copilot-map-soil-hit', 'copilot-map-soil-line'],
  terrain: ['copilot-map-terrain-fill'],
  ndvi: ['copilot-map-ndvi-fill'],
  evi: ['copilot-map-evi-fill'],
  yield: ['copilot-map-yield-fill'],
  grossMargin: ['copilot-map-margin-fill'],
}

const METRIC_LAYER_IDS = {
  terrain: 'copilot-map-terrain-fill',
  ndvi: 'copilot-map-ndvi-fill',
  evi: 'copilot-map-evi-fill',
  yield: 'copilot-map-yield-fill',
  grossMargin: 'copilot-map-margin-fill',
}

const createBaseStyle = () => ({
  version: 8,
  sources: {
    'copilot-base-map': {
      type: 'raster',
      tiles: [
        'https://mt0.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
        'https://mt1.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
        'https://mt2.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
        'https://mt3.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
      ],
      tileSize: 256,
    },
  },
  layers: [
    {
      id: 'copilot-map-background',
      type: 'background',
      paint: {'background-color': '#e9eef4'},
    },
    {
      id: 'copilot-map-base',
      type: 'raster',
      source: 'copilot-base-map',
      paint: {'raster-fade-duration': 100},
    },
  ],
})

const validFeatureCollection = (value) =>
  value?.type === 'FeatureCollection' && Array.isArray(value?.features)

const featuresOf = (value) => (validFeatureCollection(value) ? value.features : [])

const enrichAmbientFeatures = (featureCollection, ambientValues, locale) => ({
  type: 'FeatureCollection',
  features: featuresOf(featureCollection).map((feature) => {
    const descriptor = getCopilotAmbientProductivity(
      feature?.properties?.ambientClass,
      ambientValues,
      locale
    )
    return {
      ...feature,
      properties: {
        ...(feature?.properties || {}),
        ambientCode: descriptor.code,
        ambientName: descriptor.name,
        ambientColor: AMBIENT_COLORS[descriptor.code] || '#7c3aed',
      },
    }
  }),
})

const metricRange = (featureCollection, property) => {
  const values = featuresOf(featureCollection)
    .map((feature) => Number(feature?.properties?.[property]))
    .filter(Number.isFinite)

  if (values.length === 0) {
    return null
  }

  const minimum = Math.min(...values)
  const maximum = Math.max(...values)
  if (minimum === maximum) {
    const padding = Math.max(Math.abs(minimum) * 0.02, 0.01)
    return [minimum - padding, maximum + padding]
  }
  return [minimum, maximum]
}

const metricColorExpression = (property, colors, range) => {
  const [minimum, maximum] = range || [0, 1]
  const midpoint = minimum + (maximum - minimum) / 2
  return [
    'interpolate',
    ['linear'],
    ['to-number', ['get', property]],
    minimum,
    colors[0],
    midpoint,
    colors[1],
    maximum,
    colors[2],
  ]
}

const formatNumber = (value, locale, digits = 2) => {
  const number = Number(value)
  if (!Number.isFinite(number)) {
    return '-'
  }
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(number)
}

const formatDate = (value, locale) => {
  const normalized = String(value || '').trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return null
  }
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${normalized}T12:00:00Z`))
}

const formatIndexInspection = (features, prefix, locale, t) => {
  const value = firstProperty(features, `${prefix}Value`)
  const count = Number(firstProperty(features, `${prefix}ObservationCount`))
  const periodStart = formatDate(firstProperty(features, `${prefix}PeriodStart`), locale)
  const periodEnd = formatDate(firstProperty(features, `${prefix}PeriodEnd`), locale)

  return [
    formatNumber(value, locale, 2),
    Number.isFinite(count) && count > 0 ? t('integrated_analysis.map.observations', {count}) : null,
    periodStart && periodEnd
      ? t('integrated_analysis.map.period', {start: periodStart, end: periodEnd})
      : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

const firstProperty = (features, key) => {
  for (const feature of features) {
    const value = feature?.properties?.[key]
    if (value !== null && value !== undefined && value !== '') {
      return value
    }
  }
  return null
}

const hasPropertyValue = (features, key) => firstProperty(features, key) !== null

const addMetricLayer = (map, {id, source, property, colors, range}) => {
  map.addLayer({
    id,
    type: 'fill',
    source,
    filter: ['all', ['has', property], ['!=', ['get', property], null]],
    paint: {
      'fill-color': metricColorExpression(property, colors, range),
      'fill-opacity': 0.28,
    },
  })
}

const applyLayerVisibility = (map, activeLayers) => {
  MAP_LAYER_DEFINITIONS.forEach((definition) => {
    const visibility = activeLayers.includes(definition.id) ? 'visible' : 'none'
    ;(RENDER_LAYER_IDS[definition.id] || []).forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visibility)
      }
    })
  })

  const metricCount = MAP_LAYER_DEFINITIONS.filter(
    (definition) => definition.type === 'metric' && activeLayers.includes(definition.id)
  ).length
  const opacity = metricCount <= 1 ? 0.62 : metricCount === 2 ? 0.42 : 0.26
  Object.entries(METRIC_LAYER_IDS).forEach(([layerId, renderLayerId]) => {
    if (map.getLayer(renderLayerId)) {
      map.setPaintProperty(
        renderLayerId,
        'fill-opacity',
        activeLayers.includes(layerId) ? opacity : 0
      )
    }
  })
}

const SpecificCopilotIntegratedMap = ({
  mapData,
  campaignKey,
  campaignLabel,
  selectedAmbientClass,
  ambientValues,
}) => {
  const {t, i18n} = useTranslation('copilot')
  const locale = i18n.language || 'es'
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const initializedLayersRef = useRef(false)
  const activeLayersRef = useRef([])
  const [activeLayers, setActiveLayers] = useState([])
  const [inspection, setInspection] = useState(null)
  const [pdfVisualState, setPdfVisualState] = useState('pending')

  const performanceCampaign = useMemo(
    () =>
      (Array.isArray(mapData?.performanceCampaigns) ? mapData.performanceCampaigns : []).find(
        (campaign) => String(campaign?.campaignKey) === String(campaignKey)
      ) || null,
    [campaignKey, mapData]
  )
  const performanceCollection = performanceCampaign?.featureCollection || EMPTY_FEATURE_COLLECTION
  const ambientCollection = useMemo(
    () => enrichAmbientFeatures(mapData?.historicalAmbient, ambientValues, locale),
    [ambientValues, locale, mapData]
  )
  const performanceCollectionWithAmbients = useMemo(
    () => enrichAmbientFeatures(performanceCollection, ambientValues, locale),
    [ambientValues, locale, performanceCollection]
  )
  const sourceCollections = useMemo(
    () => ({
      historicalAmbient: ambientCollection,
      soilMap: mapData?.soilMap || EMPTY_FEATURE_COLLECTION,
      terrain: mapData?.terrain || EMPTY_FEATURE_COLLECTION,
      ndvi: ambientCollection,
      evi: ambientCollection,
      yield: performanceCollectionWithAmbients,
      grossMargin: performanceCollectionWithAmbients,
    }),
    [ambientCollection, mapData, performanceCollectionWithAmbients]
  )
  const availableLayerIds = useMemo(
    () =>
      MAP_LAYER_DEFINITIONS.filter((definition) => {
        if (definition.type === 'outline') {
          return featuresOf(sourceCollections[definition.id]).length > 0
        }
        return metricRange(sourceCollections[definition.id], definition.property) !== null
      }).map((definition) => definition.id),
    [sourceCollections]
  )
  const availableLayerKey = availableLayerIds.join('|')

  useEffect(() => {
    activeLayersRef.current = activeLayers
  }, [activeLayers])

  useEffect(() => {
    if (!initializedLayersRef.current) {
      initializedLayersRef.current = true
      activeLayersRef.current = availableLayerIds
      setActiveLayers(availableLayerIds)
      return
    }
    setActiveLayers((current) => {
      const next = current.filter((layerId) => availableLayerIds.includes(layerId))
      activeLayersRef.current = next
      return next
    })
  }, [availableLayerKey]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mapData?.status !== 'available' || !containerRef.current) {
      return undefined
    }

    setPdfVisualState('pending')
    if (MAPBOX_ACCESS_TOKEN) {
      mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN
    }
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: createBaseStyle(),
      center: [-63.5, -32.5],
      zoom: 13,
      maxZoom: 20,
      preserveDrawingBuffer: true,
      renderWorldCopies: false,
      attributionControl: false,
    })
    mapRef.current = map
    const onIdle = () => {
      const canvas = map.getCanvas()
      if (canvas?.width && canvas?.height) {
        setPdfVisualState('ready')
      }
    }
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            setPdfVisualState('pending')
            map.resize()
          })
    resizeObserver?.observe(containerRef.current)

    const onLoad = () => {
      map.addControl(new mapboxgl.NavigationControl({showCompass: false}), 'top-right')
      map.addSource('copilot-map-boundary-source', {
        type: 'geojson',
        data: mapData?.boundary || EMPTY_FEATURE_COLLECTION,
      })
      map.addSource('copilot-map-ambient-source', {
        type: 'geojson',
        data: ambientCollection,
      })
      map.addSource('copilot-map-soil-source', {
        type: 'geojson',
        data: mapData?.soilMap || EMPTY_FEATURE_COLLECTION,
      })
      map.addSource('copilot-map-terrain-source', {
        type: 'geojson',
        data: mapData?.terrain || EMPTY_FEATURE_COLLECTION,
      })
      map.addSource('copilot-map-performance-source', {
        type: 'geojson',
        data: performanceCollectionWithAmbients,
      })

      addMetricLayer(map, {
        id: 'copilot-map-terrain-fill',
        source: 'copilot-map-terrain-source',
        property: 'meanElevationM',
        colors: MAP_LAYER_DEFINITIONS.find((item) => item.id === 'terrain').colors,
        range: metricRange(mapData?.terrain, 'meanElevationM'),
      })
      addMetricLayer(map, {
        id: 'copilot-map-ndvi-fill',
        source: 'copilot-map-ambient-source',
        property: 'ndviValue',
        colors: MAP_LAYER_DEFINITIONS.find((item) => item.id === 'ndvi').colors,
        range: metricRange(ambientCollection, 'ndviValue'),
      })
      addMetricLayer(map, {
        id: 'copilot-map-evi-fill',
        source: 'copilot-map-ambient-source',
        property: 'eviValue',
        colors: MAP_LAYER_DEFINITIONS.find((item) => item.id === 'evi').colors,
        range: metricRange(ambientCollection, 'eviValue'),
      })
      addMetricLayer(map, {
        id: 'copilot-map-yield-fill',
        source: 'copilot-map-performance-source',
        property: 'economicYieldTonsHa',
        colors: MAP_LAYER_DEFINITIONS.find((item) => item.id === 'yield').colors,
        range: metricRange(performanceCollectionWithAmbients, 'economicYieldTonsHa'),
      })
      addMetricLayer(map, {
        id: 'copilot-map-margin-fill',
        source: 'copilot-map-performance-source',
        property: 'grossMarginUsdHa',
        colors: MAP_LAYER_DEFINITIONS.find((item) => item.id === 'grossMargin').colors,
        range: metricRange(performanceCollectionWithAmbients, 'grossMarginUsdHa'),
      })

      map.addLayer({
        id: 'copilot-map-ambient-hit',
        type: 'fill',
        source: 'copilot-map-ambient-source',
        paint: {'fill-color': ['get', 'ambientColor'], 'fill-opacity': 0.001},
      })
      map.addLayer({
        id: 'copilot-map-ambient-line',
        type: 'line',
        source: 'copilot-map-ambient-source',
        paint: {
          'line-color': ['get', 'ambientColor'],
          'line-opacity': 0.95,
          'line-width': 2.6,
        },
      })
      map.addLayer(
        {
          id: 'copilot-map-selected-ambient-fill',
          type: 'fill',
          source: 'copilot-map-ambient-source',
          filter: ['==', ['get', 'ambientClass'], String(selectedAmbientClass || '')],
          paint: {
            'fill-color': ['get', 'ambientColor'],
            'fill-opacity': 0.3,
          },
        },
        'copilot-map-ambient-line'
      )
      map.addLayer({
        id: 'copilot-map-soil-hit',
        type: 'fill',
        source: 'copilot-map-soil-source',
        paint: {'fill-color': '#111827', 'fill-opacity': 0.001},
      })
      map.addLayer({
        id: 'copilot-map-soil-line',
        type: 'line',
        source: 'copilot-map-soil-source',
        paint: {
          'line-color': '#111827',
          'line-dasharray': [2, 1.5],
          'line-opacity': 0.9,
          'line-width': 1.7,
        },
      })
      map.addLayer({
        id: 'copilot-map-boundary-casing',
        type: 'line',
        source: 'copilot-map-boundary-source',
        paint: {'line-color': 'rgba(15,23,42,0.75)', 'line-width': 5},
      })
      map.addLayer({
        id: 'copilot-map-boundary-line',
        type: 'line',
        source: 'copilot-map-boundary-source',
        paint: {'line-color': '#20a4f3', 'line-width': 2.5},
      })

      applyLayerVisibility(map, activeLayersRef.current)

      try {
        const bounds = turfBbox(mapData?.boundary || EMPTY_FEATURE_COLLECTION)
        if (bounds.every(Number.isFinite)) {
          map.fitBounds(
            [
              [bounds[0], bounds[1]],
              [bounds[2], bounds[3]],
            ],
            {duration: 0, padding: 28}
          )
        }
      } catch (error) {
        // Keep the fallback view when the boundary is not usable.
      }

      map.getCanvas().style.cursor = 'crosshair'
      map.on('click', (event) => {
        const queryLayers = Object.values(RENDER_LAYER_IDS)
          .flat()
          .filter((layerId) => map.getLayer(layerId))
        const features = map.queryRenderedFeatures(event.point, {layers: queryLayers})
        setInspection(features.length > 0 ? features : null)
      })
    }

    map.on('load', onLoad)
    map.on('idle', onIdle)
    return () => {
      resizeObserver?.disconnect()
      map.off('idle', onIdle)
      map.remove()
      mapRef.current = null
    }
  }, [mapData]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapRef.current
    if (!map?.getSource('copilot-map-performance-source')) {
      return
    }
    setPdfVisualState('pending')
    map.getSource('copilot-map-performance-source').setData(performanceCollectionWithAmbients)
    ;['yield', 'grossMargin'].forEach((layerId) => {
      const definition = MAP_LAYER_DEFINITIONS.find((item) => item.id === layerId)
      const renderLayerId = METRIC_LAYER_IDS[layerId]
      if (!definition || !map.getLayer(renderLayerId)) {
        return
      }
      map.setPaintProperty(
        renderLayerId,
        'fill-color',
        metricColorExpression(
          definition.property,
          definition.colors,
          metricRange(performanceCollectionWithAmbients, definition.property)
        )
      )
    })
    setInspection(null)
  }, [performanceCollectionWithAmbients])

  useEffect(() => {
    const map = mapRef.current
    if (!map?.getLayer('copilot-map-selected-ambient-fill')) {
      return
    }
    setPdfVisualState('pending')
    map.setFilter('copilot-map-selected-ambient-fill', [
      '==',
      ['get', 'ambientClass'],
      String(selectedAmbientClass || ''),
    ])
  }, [selectedAmbientClass])

  useEffect(() => {
    const map = mapRef.current
    if (!map?.isStyleLoaded()) {
      return
    }
    setPdfVisualState('pending')
    applyLayerVisibility(map, activeLayers)
    setInspection(null)
  }, [activeLayers])

  if (mapData?.status !== 'available') {
    return null
  }

  const activeDefinitions = MAP_LAYER_DEFINITIONS.filter((definition) =>
    activeLayers.includes(definition.id)
  )
  const legendItems = activeDefinitions.map((definition) => ({
    ...definition,
    range:
      definition.type === 'metric'
        ? metricRange(sourceCollections[definition.id], definition.property)
        : null,
  }))

  const inspectionFeatures = Array.isArray(inspection) ? inspection : []
  const inspectedAmbientClass = firstProperty(inspectionFeatures, 'ambientClass')
  const inspectedSoilUnit = firstProperty(inspectionFeatures, 'soilUnit')
  const inspectedSoilProductivityIndex =
    firstProperty(inspectionFeatures, 'soilProductivityIndex') ??
    firstProperty(inspectionFeatures, 'productivityIndex')
  const inspectedDescriptor = inspectedAmbientClass
    ? getCopilotAmbientProductivity(inspectedAmbientClass, ambientValues, locale)
    : null
  const inspectionRows = [
    activeLayers.includes('historicalAmbient') && inspectedDescriptor
      ? [t('integrated_analysis.map.layers.historical_ambient'), inspectedDescriptor.label]
      : null,
    activeLayers.includes('soilMap') && inspectedSoilUnit
      ? [
          t('integrated_analysis.map.layers.soil_map'),
          [
            inspectedSoilUnit,
            inspectedSoilProductivityIndex !== null
              ? `IP ${formatNumber(inspectedSoilProductivityIndex, locale, 0)}`
              : null,
          ]
            .filter(Boolean)
            .join(' · '),
        ]
      : null,
    activeLayers.includes('terrain') && hasPropertyValue(inspectionFeatures, 'meanElevationM')
      ? [
          t('integrated_analysis.map.layers.terrain'),
          `${formatNumber(firstProperty(inspectionFeatures, 'meanElevationM'), locale, 1)} m`,
        ]
      : null,
    activeLayers.includes('ndvi') && hasPropertyValue(inspectionFeatures, 'ndviValue')
      ? [
          t('integrated_analysis.map.layers.ndvi'),
          formatIndexInspection(inspectionFeatures, 'ndvi', locale, t),
        ]
      : null,
    activeLayers.includes('evi') && hasPropertyValue(inspectionFeatures, 'eviValue')
      ? [
          t('integrated_analysis.map.layers.evi'),
          formatIndexInspection(inspectionFeatures, 'evi', locale, t),
        ]
      : null,
    activeLayers.includes('yield') && hasPropertyValue(inspectionFeatures, 'economicYieldTonsHa')
      ? [
          t('integrated_analysis.map.layers.yield'),
          `${formatNumber(
            firstProperty(inspectionFeatures, 'economicYieldTonsHa'),
            locale,
            2
          )} t/ha`,
        ]
      : null,
    activeLayers.includes('grossMargin') && hasPropertyValue(inspectionFeatures, 'grossMarginUsdHa')
      ? [
          t('integrated_analysis.map.layers.gross_margin'),
          `${formatNumber(
            firstProperty(inspectionFeatures, 'grossMarginUsdHa'),
            locale,
            2
          )} USD/ha`,
        ]
      : null,
  ].filter(Boolean)

  return (
    <article
      className='specific-copilot-integrated-map'
      data-pdf-visual-kind='map'
      data-pdf-visual-id='integrated-map'
      data-pdf-visual-state={pdfVisualState}
    >
      <div className='specific-copilot-integrated-map__heading'>
        <div>
          <h5>{t('integrated_analysis.map.title')}</h5>
          <p>{t('integrated_analysis.map.subtitle')}</p>
        </div>
        <div className='specific-copilot-integrated-map__commands'>
          <button
            type='button'
            onClick={() => setActiveLayers(availableLayerIds)}
            disabled={activeLayers.length === availableLayerIds.length}
          >
            {t('integrated_analysis.map.select_all')}
          </button>
          <button type='button' onClick={() => setActiveLayers([])} disabled={!activeLayers.length}>
            {t('integrated_analysis.map.select_none')}
          </button>
        </div>
      </div>

      <div className='specific-copilot-integrated-map__layers'>
        {MAP_LAYER_DEFINITIONS.map((definition) => {
          const available = availableLayerIds.includes(definition.id)
          const checked = activeLayers.includes(definition.id)
          return (
            <label
              key={definition.id}
              className={`specific-copilot-integrated-map__layer${checked ? ' is-active' : ''}${
                available ? '' : ' is-disabled'
              }`}
            >
              <input
                type='checkbox'
                checked={checked}
                disabled={!available}
                onChange={() =>
                  setActiveLayers((current) =>
                    current.includes(definition.id)
                      ? current.filter((layerId) => layerId !== definition.id)
                      : [...current, definition.id]
                  )
                }
              />
              <span
                className={`specific-copilot-integrated-map__swatch specific-copilot-integrated-map__swatch--${definition.type}`}
                style={
                  definition.type === 'metric'
                    ? {background: `linear-gradient(90deg, ${definition.colors.join(', ')})`}
                    : {borderColor: definition.color}
                }
              />
              <span>{t(`integrated_analysis.map.layers.${definition.translationKey}`)}</span>
              {!available && <small>{t('integrated_analysis.map.no_data_short')}</small>}
            </label>
          )
        })}
      </div>

      <div className='specific-copilot-integrated-map__canvas-wrap'>
        <div ref={containerRef} className='specific-copilot-integrated-map__canvas' />
        {activeLayers.length === 0 && (
          <div className='specific-copilot-integrated-map__empty-selection'>
            {t('integrated_analysis.map.empty_selection')}
          </div>
        )}
      </div>

      {legendItems.length > 0 && (
        <div
          className='specific-copilot-integrated-map__legend'
          aria-label={t('integrated_analysis.map.legend')}
        >
          {legendItems.map((item) => (
            <div key={item.id} className='specific-copilot-integrated-map__legend-item'>
              <span>{t(`integrated_analysis.map.layers.${item.translationKey}`)}</span>
              {item.type === 'metric' && item.range ? (
                <>
                  <i style={{background: `linear-gradient(90deg, ${item.colors.join(', ')})`}} />
                  <small>
                    {formatNumber(item.range[0], locale, item.id === 'terrain' ? 1 : 2)}
                    {item.unit ? ` ${item.unit}` : ''} –{' '}
                    {formatNumber(item.range[1], locale, item.id === 'terrain' ? 1 : 2)}
                    {item.unit ? ` ${item.unit}` : ''}
                  </small>
                </>
              ) : (
                <i className='is-line' style={{borderColor: item.color}} />
              )}
            </div>
          ))}
        </div>
      )}

      <div className='specific-copilot-integrated-map__inspection' aria-live='polite'>
        <strong>{t('integrated_analysis.map.inspection_title')}</strong>
        {inspectionRows.length > 0 ? (
          <dl>
            {inspectionRows.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
            {(activeLayers.includes('yield') || activeLayers.includes('grossMargin')) && (
              <div>
                <dt>{t('integrated_analysis.columns.campaign')}</dt>
                <dd>{campaignLabel || '-'}</dd>
              </div>
            )}
          </dl>
        ) : (
          <p>{t('integrated_analysis.map.inspection_empty')}</p>
        )}
      </div>
    </article>
  )
}

export default SpecificCopilotIntegratedMap
